import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MapPinned, Scissors, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const COMMUNES_PAR_DEFAUT = [
  { code: "11262", nom: "Narbonne" },
  { code: "11069", nom: "Carcassonne" },
];
const TAILLES = ["1", "2", "3", "5", "8", "10", "15"];

/** Pré-découpage automatique d'une commune en quartiers non affectés. */
export function PredecoupagePanel({ onDone }) {
  const [choix, setChoix] = useState(() => new Set(COMMUNES_PAR_DEFAUT.map((c) => c.code)));
  const [autre, setAutre] = useState("");
  const [taille, setTaille] = useState("5");
  const [remplacer, setRemplacer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resume, setResume] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);

  const loadResume = useCallback(async () => {
    try { const { data } = await api.get("/zones/predecoupage"); setResume(data); } catch {}
  }, []);
  useEffect(() => { loadResume(); }, [loadResume]);

  const toggle = (code) => setChoix((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n; });

  const lancer = async () => {
    const communes = [...choix];
    if (/^\d{5}$/.test(autre.trim()) || /^\d{2}[AB]\d{3}$/i.test(autre.trim())) communes.push(autre.trim().toUpperCase());
    if (!communes.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/zones/predecoupage", { communes, taille_max_km2: parseFloat(taille), remplacer });
      data.resultats.forEach((r) => {
        toast.success(
          r.source === "iris"
            ? `${r.nom} : ${r.quartiers} quartiers → ${r.zones} zones à affecter`
            : `${r.nom} : quartiers IGN indisponibles, découpage en ${r.zones} secteurs réguliers`,
        );
      });
      setRemplacer(false);
      loadResume();
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const supprimer = async (c) => {
    try {
      const { data } = await api.delete(`/zones/predecoupage/${c.code_insee}`);
      toast.success(`${c.nom} : ${data.supprimees} zones supprimées`);
      setConfirmDel(null);
      loadResume();
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4 mb-4" data-testid="predecoupage-panel">
      <div>
        <p className="font-semibold flex items-center gap-2"><Scissors size={17} /> Pré-découpage automatique par quartier</p>
        <p className="text-sm text-muted-foreground mt-1">
          Crée automatiquement des zones <b>non affectées</b> pour chaque quartier de la commune (quartiers IRIS de l'INSEE, contours IGN).
          Un quartier plus grand que la taille maximale est coupé en plusieurs morceaux. Il suffit ensuite de cliquer sur chaque morceau
          sur la carte et de choisir sa tournée — sans rien dessiner.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1.5">
          <Label>Communes</Label>
          <div className="flex flex-wrap gap-2">
            {COMMUNES_PAR_DEFAUT.map((c) => (
              <label key={c.code} className={`flex items-center gap-2 px-3 h-9 rounded-md border text-sm cursor-pointer ${choix.has(c.code) ? "border-primary bg-primary/10" : "border-border"}`}>
                <input type="checkbox" checked={choix.has(c.code)} onChange={() => toggle(c.code)} className="accent-[hsl(var(--primary))]" data-testid={`pred-${c.code}`} />
                {c.nom}
              </label>
            ))}
            <Input value={autre} onChange={(e) => setAutre(e.target.value)} placeholder="Autre (code INSEE)" className="w-40 h-9" title="Code INSEE de la commune, ex. 11203 pour Lézignan-Corbières" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Taille max. d'un morceau</Label>
          <Select value={taille} onValueChange={setTaille}>
            <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{TAILLES.map((t) => <SelectItem key={t} value={t}>{t} km²</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm h-9">
          <input type="checkbox" checked={remplacer} onChange={(e) => setRemplacer(e.target.checked)} className="accent-[hsl(var(--primary))]" />
          Remplacer un pré-découpage existant
        </label>
        <Button onClick={lancer} disabled={busy} data-testid="pred-lancer">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Scissors size={14} />} Créer le pré-découpage
        </Button>
      </div>
      {remplacer && (
        <p className="text-xs text-amber-500">Attention : « Remplacer » supprime toutes les zones du pré-découpage de ces communes, y compris celles déjà affectées.</p>
      )}

      {resume.length > 0 && (
        <div className="border-t border-border pt-3 space-y-2">
          {resume.map((c) => {
            const pct = c.total ? Math.round((c.affectees / c.total) * 100) : 0;
            return (
              <div key={c.code_insee} className="flex flex-wrap items-center gap-3 text-sm" data-testid={`pred-resume-${c.code_insee}`}>
                <span className="font-medium w-32">{c.nom}</span>
                <div className="flex-1 min-w-[140px] h-2 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs text-muted-foreground w-40">{c.affectees} / {c.total} zones affectées{c.source === "secteurs" ? " (secteurs)" : ""}</span>
                <Button size="sm" variant="ghost" asChild><Link to="/carte"><MapPinned size={14} /> Carte</Link></Button>
                {confirmDel === c.code_insee ? (
                  <Button size="sm" variant="destructive" onClick={() => supprimer(c)}>Confirmer</Button>
                ) : (
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirmDel(c.code_insee)} title="Supprimer ce pré-découpage"><Trash2 size={14} /></Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
